import { FileReference } from "../components/OI/OiReport";
import { getFilenameFromContentDisposition } from "../utils/httpFileMeta";

export enum AnalysisResultStatus {
  InProgress = 0,
  Completed = 2,
  // Never persisted: a result that has not been created yet because nobody has started editing it.
  NotStarted = 3,
}

// Only InProgress and Completed are ever persisted; anything else read from the server is treated as in progress.
export function normalizeAnalysisResultStatus(status: AnalysisResultStatus): AnalysisResultStatus {
  return status === AnalysisResultStatus.Completed ? AnalysisResultStatus.Completed : AnalysisResultStatus.InProgress;
}

export interface AnalysisResultSectionFile extends FileReference {}

export interface AnalysisResultSummaryRow {
  sampleId: string;
  density?: string;
  hc?: string;
  co?: string;
  o?: string;
  coM?: string;
  mm?: string;
  mi?: string;
  ma?: string;
  gradient?: string;
}

export interface AnalysisResultCompletion {
  isComplete: boolean;
  completedBy: string;
  completedDate: string;
}

export interface AnalysisResultSectionRow {
  rowLabel: string;
  sampleValues: Record<string, string>;
  /** Extra per-sample values keyed by field then sample id, e.g. LOM Porosity { mi: { sampleId: "A02B00C00" }, ma: {...} }. */
  sampleFields?: Record<string, Record<string, string>>;
  files?: AnalysisResultSectionFile[];
  comment?: string;
}

export interface AnalysisResultSection {
  analysisKey: string;
  title: string;
  status: AnalysisResultStatus;
  startedBy: string;
  startedDate: string;
  rows: AnalysisResultSectionRow[];
  files: AnalysisResultSectionFile[];
  comment: string;
  completion: AnalysisResultCompletion;
}

export interface AnalysisResultData {
  summaryRows: AnalysisResultSummaryRow[];
  summaryNote: string;
  densityMeasurementTemperature: string;
  densityCorrectionFactor: string;
  chemistryRows: AnalysisResultSectionRow[];
  sections: AnalysisResultSection[];
}

export interface OiAnalysisResultDTO {
  uuid: string;
  analysisOrderUuid: string;
  analysisOrderId?: string;
  jsonData: string;
  createdAt?: string;
  lastUpdated?: string;
  status: AnalysisResultStatus;
  isArchived?: boolean;
  author?: string;
}

export interface OiAnalysisResultDTOMinimal {
  uuid: string;
  analysisOrderUuid: string;
  analysisOrderId?: string;
  status: AnalysisResultStatus;
}

export interface OiAnalysisResultGrouped {
  notStarted: OiAnalysisResultDTOMinimal[];
  inProgress: OiAnalysisResultDTOMinimal[];
  completed: OiAnalysisResultDTOMinimal[];
}

const BASE_URL = `${process.env.REACT_APP_API_URL}/oi-analysis-result`;

export const ANALYSIS_RESULT_SAVE_ERROR = "Could not save analysis result.";

export interface OpenAnalysisResultOutcome {
  uuid: string | null;
  error?: string;
}

export interface UpdateAnalysisResultOutcome {
  ok: boolean;
  error?: string;
}

// Validation failures come back as RFC 7807 ProblemDetails; fall back to a generic message otherwise.
async function readProblemDetail(response: Response): Promise<string> {
  try {
    const problem = await response.json();
    if (typeof problem?.detail === "string" && problem.detail.trim()) {
      return problem.detail;
    }
  } catch {
    // Body is empty or not JSON.
  }
  return ANALYSIS_RESULT_SAVE_ERROR;
}

export async function OpenOiAnalysisResult(analysisOrderUuid: string, payload: string, author: string): Promise<OpenAnalysisResultOutcome> {
  try {
    const response = await fetch(`${BASE_URL}/analysis-order/${analysisOrderUuid}/open`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        analysisOrderUuid,
        jsonData: payload,
        author,
        status: AnalysisResultStatus.InProgress,
      }),
    });

    if (response.ok) {
      return { uuid: await response.text() };
    }

    return { uuid: null, error: await readProblemDetail(response) };
  } catch (error) {
    console.error("Failed to open OiAnalysisResult", error);
    return { uuid: null, error: ANALYSIS_RESULT_SAVE_ERROR };
  }
}

export async function UpdateOiAnalysisResult(uuid: string, payload: string, status: AnalysisResultStatus): Promise<UpdateAnalysisResultOutcome> {
  try {
    const response = await fetch(`${BASE_URL}/${uuid}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        jsonData: payload,
        status,
      }),
    });

    if (response.ok) {
      return { ok: true };
    }

    return { ok: false, error: await readProblemDetail(response) };
  } catch (error) {
    console.error("Failed to update OiAnalysisResult", error);
    return { ok: false, error: ANALYSIS_RESULT_SAVE_ERROR };
  }
}

export async function GetOiAnalysisResult(uuid: string): Promise<OiAnalysisResultDTO | null> {
  try {
    const response = await fetch(`${BASE_URL}/${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      return await response.json();
    }

    return null;
  } catch (error) {
    console.error("Failed to fetch OiAnalysisResult", error);
    return null;
  }
}

export async function GetOiAnalysisResultByAnalysisOrderUuid(analysisOrderUuid: string): Promise<OiAnalysisResultDTO | null> {
  try {
    const response = await fetch(`${BASE_URL}/analysis-order/${analysisOrderUuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      return await response.json();
    }

    return null;
  } catch (error) {
    console.error("Failed to fetch OiAnalysisResult by analysis order", error);
    return null;
  }
}

export async function getAllOiAnalysisResultsGrouped(): Promise<OiAnalysisResultGrouped | null> {
  try {
    const response = await fetch(`${BASE_URL}/all-grouped`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      return await response.json();
    }

    return null;
  } catch (error) {
    console.error("Failed to fetch grouped OiAnalysisResults", error);
    return null;
  }
}

export async function getAnalysisResultsFiltered(query: string): Promise<OiAnalysisResultGrouped | null> {
  try {
    const response = await fetch(`${BASE_URL}/all-filtered?search=${encodeURIComponent(query)}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.ok) {
      return await response.json();
    }

    return null;
  } catch (error) {
    console.error("Failed to fetch filtered OiAnalysisResults", error);
    return null;
  }
}

export const emptyAnalysisResultData = (): AnalysisResultData => ({
  summaryRows: [],
  summaryNote: "Summary of key results for all samples. Values are collected from the respective analysis group.",
  densityMeasurementTemperature: "",
  densityCorrectionFactor: "",
  chemistryRows: [],
  sections: [],
});
/** Downloads the server-rendered PDF of a completed result. Files are rendered into the PDF only for the listed sections. */
export async function DownloadAnalysisResultPdf(uuid: string, includeFilesForSections: string[], fallbackFilename: string): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}/${uuid}/pdf`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/pdf",
      },
      body: JSON.stringify({ includeFilesForSections }),
    });

    if (!response.ok) {
      console.error("Failed to download analysis result PDF", response.status, response.statusText);
      return false;
    }

    const blob = await response.blob();
    const filename = getFilenameFromContentDisposition(response.headers.get("content-disposition")) ?? fallbackFilename;
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    return true;
  } catch (error) {
    console.error("Failed to download analysis result PDF", error);
    return false;
  }
}
